import React from "react";

export const projects = [
  {
    title: "Intelligent Responder Prediction System",
    category: "Machine Learning / Emergency Response",
    description:
      "Designed and trained machine learning models to predict responder behaviour, including acceptance, decline, and stand-down likelihood in real-time emergency scenarios.",
    impact:
      "Enables predictive dispatch and improves response efficiency by leveraging historical responder behaviour and contextual features.",
    tech: ["Python", "Pandas", "Scikit-learn", "Node.js", "PostgreSQL"],
  },
  {
    title: "Callout Classification with Foundation Models",
    category: "AI / NLP / Emergency Intelligence",
    description:
      "Implemented classification pipelines using foundational models to automatically categorize emergency callouts based on contextual and textual data.",
    impact:
      "Improves decision-making and prioritisation in the control centre by reducing manual classification effort and increasing consistency.",
    tech: ["LLMs", "OpenAI", "Python", "Node.js", "NLP"],
  },
  {
    title: "Responder Routing Algorithms",
    category: "Algorithms / Optimization / Dispatch Systems",
    description:
      "Designed and optimized routing algorithms to determine the best responders based on ETA, distance, and contextual constraints, including batched API strategies to minimize external API calls.",
    impact:
      "Reduced routing API costs and improved dispatch efficiency by selecting optimal responders faster and more accurately.",
    tech: ["TypeScript", "Node.js", "Google Maps", "Mapbox", "Algorithms"],
  },
  {
    title: "AI Workflow Agent for False Callout Filtering",
    category: "AI Agents / Emergency Operations Automation",
    description:
      "Designed and implemented an AI-driven workflow agent that handles potential false-trigger emergency callouts end-to-end. The agent interacts directly with users via automated calls, asks structured verification questions, and evaluates whether the callout is valid or a false trigger.",
    impact:
      "Reduced unnecessary workload on human controllers by filtering false or low-confidence callouts, allowing them to focus on genuine emergencies requiring immediate attention. Improved operational efficiency and response prioritization.",
    tech: ["TypeScript", "Node.js", "LLMs", "OpenAI", "Voice/Telephony APIs", "AI Agents"],
  },
  {
    title: "Multi-Provider Routing Engine",
    category: "System Design / Routing Infrastructure",
    description:
      "Designed a routing system supporting both Mapbox and Google Maps using a provider selection strategy based on responder app versions.",
    impact:
      "Enabled seamless migration between providers without disrupting legacy mobile applications.",
    tech: ["TypeScript", "Google Maps", "Mapbox", "Strategy Pattern", "Caching"],
  },
  {
    title: "Scheduled Services System",
    category: "Backend Systems / Time-Based Orchestration",
    description:
      "Built a flexible scheduling engine supporting complex recurrence rules (weekly, monthly, time windows) and automated service execution.",
    impact:
      "Enabled large-scale automation of scheduled emergency-related workflows and notifications.",
    tech: ["TypeScript", "Node.js", "PostgreSQL", "Cron Jobs"],
  },
  {
    title: "Real-Time Emergency Dispatch Backend",
    category: "Backend Systems / High Availability",
    description:
      "Built and maintained scalable backend services powering real-time emergency dispatch across multiple countries.",
    impact:
      "Ensures high availability and reliability for life-critical systems handling real-time incidents.",
    tech: ["TypeScript", "Node.js", "Express", "Redis", "AWS"],
  },
  {
    title: "Responder Event-Driven Integration",
    category: "Event-Driven Architecture / Distributed Systems",
    description:
      "Implemented event-driven workflows for responder lifecycle events (arrival, dispatch, stand-down, redistribution) across multiple services.",
    impact:
      "Improved system observability and decoupling, enabling faster feature development and more reliable state transitions.",
    tech: ["Node.js", "Event Streams", "PostgreSQL", "Microservices"],
  },
  {
    title: "Control Centre Frontend Platform",
    category: "Frontend Engineering / Operations Systems",
    description:
      "Contributed to the development of the Control Centre interface used by operators to manage real-time emergency incidents and responder coordination.",
    impact:
      "Improved operational visibility and decision-making for controllers managing high-pressure emergency scenarios.",
    tech: ["React", "TypeScript", "WebSockets", "REST APIs"],
  },
  {
    title: "Callout Ratings & Notification System",
    category: "Backend Systems / User Engagement",
    description:
      "Designed notification workflows and SQL-driven pipelines to request and track callout ratings with multi-step follow-ups (24h/48h).",
    impact:
      "Improved feedback collection rates and enabled better service quality monitoring.",
    tech: ["PostgreSQL", "Node.js", "SQL Optimization", "Cron Jobs"],
  },
  {
    title: "Responder Welfare Monitoring System",
    category: "Safety Systems / Alerting",
    description:
      "Built a monitoring system to track responders post-arrival and trigger normal and critical alerts based on configurable time thresholds.",
    impact:
      "Enhanced responder safety by ensuring timely welfare checks and escalation mechanisms.",
    tech: ["Node.js", "AWS Parameter Store", "Cron Jobs", "Notifications"],
  },
  {
    title: "Address Mapping & Geocoding Engine",
    category: "Data Engineering / Location Intelligence",
    description:
      "Designed region-aware address parsing and mapping logic using Google Geocoding, including fallback heuristics and formatted address extraction for missing fields.",
    impact:
      "Improved address accuracy across multiple regions (UK, South Africa, Kenya) for responders and dispatch systems.",
    tech: ["TypeScript", "Google Maps API", "Mapbox", "Data Parsing"],
  },
  {
    title: "White-Label Platform Configuration System",
    category: "Platform Engineering / Multi-Tenant Systems",
    description:
      "Implemented configuration management for 200+ white-label applications, including feature toggles and per-client behaviour control.",
    impact:
      "Enabled scalable customization across multiple clients without code duplication.",
    tech: ["Node.js", "PostgreSQL", "AWS", "Multi-Tenant Architecture"],
  },
  {
    title: "Callout Event System & Lifecycle Management",
    category: "Domain Systems / Event Modeling",
    description:
      "Designed and extended the callout event system to support lifecycle events such as cancellations, redistributions, stand-downs, and reviews.",
    impact:
      "Improved traceability and control of emergency workflows across the platform.",
    tech: ["Node.js", "PostgreSQL", "Event Modeling"],
  },
  {
    title: "Microservices Migration",
    category: "Architecture / Scalability",
    description:
      "Contributed to migrating legacy monolithic systems into modular microservices with clear domain boundaries.",
    impact:
      "Reduced system coupling and improved scalability, deployment, and maintainability.",
    tech: ["Docker", "Node.js", "PostgreSQL", "CI/CD", "AWS"],
  },
  {
    title: "AI Workflow Automation Agents",
    category: "AI Engineering / Developer Productivity",
    description:
      "Built AI-powered agents to automate backend workflows, assist with code generation, and enhance engineering productivity.",
    impact:
      "Accelerated development cycles and introduced intelligent automation into backend processes.",
    tech: ["OpenAI", "LangChain", "TypeScript", "LLMs"],
  },
  {
    title: "PCM Integration & Data Synchronization",
    category: "Data Engineering / Integration",
    description:
      "Designed and implemented integration solutions for synchronizing data between the PCM system and other internal platforms.",
    impact:
      "Improved data consistency and reduced manual reconciliation efforts across integrated systems.",
    tech: ["Node.js", "PostgreSQL", "REST APIs", "Data Pipelines"],
  },
  {
    title: "IoT Panic Button System Integration",
    category: "IoT / Hardware Integration",
    description:
      "Integrated IoT panic button devices with the central dispatch system, enabling real-time alerting and location tracking.",
    impact:
      "Enhanced emergency response capabilities and improved responder safety.",
    tech: ["IoT", "Node.js", "WebSocket", "Location Services"],
  },
  {
    title: "Agent-Scoped AI Suggestions Platform",
    category: "AI Engineering / Control Centre",
    description:
      "Designed and implemented AI-powered suggestion workflows within the Control Centre, enabling incident classification and incident summary recommendations through agent-scoped interactions and controlled review processes.",
    impact:
      "Improved controller productivity and consistency by providing contextual AI assistance while maintaining human oversight.",
    tech: ["React", "TypeScript", "OpenAI", "LLMs", "Node.js"],
  },
  {
    title: "AI Incident Summary Generation",
    category: "Generative AI / Emergency Intelligence",
    description:
      "Built AI-powered summarization capabilities that generate structured incident summaries from emergency callout information and operational data.",
    impact:
      "Reduced manual documentation effort and improved the quality and consistency of incident records.",
    tech: ["OpenAI", "LLMs", "NLP", "TypeScript", "Node.js"],
  },
  {
    title: "Emergency Operations Analytics Platform",
    category: "Data Engineering / Analytics",
    description:
      "Developed operational reporting and analytics solutions for responder performance, dispatch efficiency, callout activity, and service quality monitoring.",
    impact:
      "Provided actionable insights for operational decision-making and continuous service improvement.",
    tech: ["PostgreSQL", "SQL", "Data Analysis", "Node.js"],
  },
  {
    title: "Supplier & Responder Migration Framework",
    category: "Data Migration / Platform Operations",
    description:
      "Designed tooling and migration processes for transferring responders between suppliers while preserving operational integrity and historical callout relationships.",
    impact:
      "Reduced manual migration effort and improved administrative efficiency for large-scale responder management.",
    tech: ["PostgreSQL", "TypeScript", "Node.js", "Data Migration"],
  },
  {
    title: "AI-Human Callout Feedback Differentiation",
    category: "AI Governance / Customer Feedback",
    description:
      "Implemented mechanisms to distinguish customer feedback and rating workflows between AI-handled and human-handled callouts.",
    impact:
      "Enabled measurement and comparison of AI-assisted operations against human performance using customer feedback data.",
    tech: ["Node.js", "PostgreSQL", "TypeScript", "Feature Toggles"],
  },
];

const getBadgeLabel = (project) => {
  const category = (project.category || "").toLowerCase();

  if (category.includes("machine learning") || category.includes("ai")) {
    return "ML";
  }

  if (category.includes("backend")) {
    return "API";
  }

  if (category.includes("system")) {
    return "SYS";
  }

  if (category.includes("architecture")) {
    return "ARC";
  }

  return "SYS";
};

const Projects = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-grey-900 to-slate-900 p-8 pt-24">
      <div className="container mx-auto">
        <div className="mb-12 text-center">
          <h2 className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-5xl font-bold text-transparent">
            Projects
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {projects.map((project, index) => (
            <article
              key={`${project.title}-${index}`}
              className="rounded-xl border border-gray-700/50 bg-gradient-to-br from-gray-800/90 to-gray-900/90 p-6 shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:border-cyan-500/50 hover:shadow-cyan-500/20"
            >
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-800/80 text-2xl font-semibold tracking-[0.08em] text-cyan-400">
                {getBadgeLabel(project)}
              </div>

              <p className="mb-4 text-sm font-medium uppercase tracking-[0.28em] text-emerald-400">
                {project.category}
              </p>

              <h3 className="mb-4 text-xl font-semibold leading-tight text-white">
                {project.title}
              </h3>

              <p className="mb-6 text-sm leading-relaxed text-gray-300">
                {project.description}
              </p>

              <div className="mb-6 rounded-xl border border-cyan-500/20 bg-slate-800/40 p-4">
                <p className="text-sm leading-relaxed text-gray-300">
                  <span className="font-semibold text-white">Impact:</span>{" "}
                  {project.impact}
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                {project.tech.map((tech) => (
                  <span
                    key={`${project.title}-${tech}`}
                    className="rounded-full border border-gray-600/50 bg-gray-700/30 px-4 py-2 text-sm font-medium text-gray-300"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Projects;
